import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listActiveTimers,
  listTaskLiveTimers,
  pauseTimer,
  resumeTimer,
  startTimer,
  stopTimer,
  type ActiveTimer,
} from '../lib/timersApi';

/**
 * The caller's live task timers, shared by the global bar and the task detail
 * buttons through the one `['task-timers']` key.
 *
 * The query-client defaults (staleTime 60s, refetchOnWindowFocus off) are wrong
 * for a clock, so all three are overridden here. Tasks open in new tabs, so a
 * second tab is guaranteed: without these it would show an already-stopped
 * timer for minutes and never refresh when you switch back to it.
 */
export function useActiveTimers() {
  return useQuery({
    queryKey: ['task-timers'],
    queryFn: listActiveTimers,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}

/**
 * Everyone's live timers on one task. The clock ticks locally; the 15s poll is
 * how a colleague's pause, resume or stop reaches this screen.
 */
export function useTaskLiveTimers(taskId: string) {
  return useQuery({
    queryKey: ['task-live-timers', taskId],
    queryFn: () => listTaskLiveTimers(taskId),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
  });
}

/**
 * A "now" in server time that advances once a second — or once per
 * `intervalMs` for a screen that only needs a coarse clock (the calendar reads
 * it to decide which reminders are firing; re-rendering a month of chips every
 * second for that would be waste).
 *
 * Two things it must get right: the returned value is the CURRENT clock reading
 * (callers recompute elapsed time from it rather than incrementing a counter),
 * and it is corrected by `serverNow - Date.now()` so a client whose clock is
 * behind does not render negative durations.
 */
export function useTimerNow(serverNow: string | undefined, intervalMs = 1000): number {
  const [, setTick] = useState(0);

  // The offset is measured ONCE per server reading, not per render. Deriving it
  // inline was a frozen clock: `Date.now() + (serverNow - Date.now())` cancels
  // down to `serverNow`, so the display sat on the last fetch and only moved
  // when the query refetched.
  const offsetRef = useRef(0);
  const measuredFor = useRef<string | undefined>(undefined);
  if (serverNow !== undefined && serverNow !== measuredFor.current) {
    measuredFor.current = serverNow;
    const parsed = Date.parse(serverNow);
    offsetRef.current = Number.isNaN(parsed) ? 0 : parsed - Date.now();
  }

  useEffect(() => {
    // One interval, whose only job is to force a re-render.
    const id = window.setInterval(() => setTick((n) => n + 1), intervalMs);
    const onVisible = () => setTick((n) => n + 1);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs]);

  return Date.now() + offsetRef.current;
}

/** Find the caller's timer for one task, if any. */
export function timerForTask(
  timers: ActiveTimer[] | undefined,
  taskId: string,
): ActiveTimer | undefined {
  return timers?.find((t) => t.taskId === taskId);
}

// Stopping a timer writes a TimeEntry, so it moves everything a manual worklog
// entry moves (see useTimeEntries) — plus the timer list itself. `task-timeline`
// is in there because Stop also logs an activity row.
function invalidate(qc: ReturnType<typeof useQueryClient>, taskId: string) {
  void qc.invalidateQueries({ queryKey: ['task-timers'] });
  void qc.invalidateQueries({ queryKey: ['task-live-timers', taskId] });
  void qc.invalidateQueries({ queryKey: ['time-entries', taskId] });
  void qc.invalidateQueries({ queryKey: ['task', taskId] });
  void qc.invalidateQueries({ queryKey: ['task-timeline', taskId] });
  void qc.invalidateQueries({ queryKey: ['tasks'] });
}

export function useStartTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => startTimer(taskId),
    onSuccess: (_data, taskId) => invalidate(qc, taskId),
  });
}

export function usePauseTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => pauseTimer(taskId),
    onSuccess: (_data, taskId) => invalidate(qc, taskId),
    // A 404 means the task (or the timer) is already gone — refetching drops the
    // phantom row instead of leaving a stuck error on screen.
    onError: (_err, taskId) => invalidate(qc, taskId),
  });
}

export function useResumeTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => resumeTimer(taskId),
    onSuccess: (_data, taskId) => invalidate(qc, taskId),
    onError: (_err, taskId) => invalidate(qc, taskId),
  });
}

export function useStopTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => stopTimer(taskId),
    onSuccess: (_data, taskId) => invalidate(qc, taskId),
    onError: (_err, taskId) => invalidate(qc, taskId),
  });
}
